import React, { useState } from 'react';
import { X, Upload } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import documentManagementService from '../../../services/documentManagementService';
import { useAuth } from '../../../contexts/AuthContext';

const DocumentUploadModal = ({ employees, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    employeeId: '',
    category: 'personal',
    documentName: '',
    documentDate: new Date()?.toISOString()?.split('T')?.[0],
    description: '',
    tags: '',
    file: null
  });

  const categories = [
    { value: 'payslip', label: 'Payslip' },
    { value: 'employment_letter', label: 'Employment Letter' },
    { value: 'contract', label: 'Contract' },
    { value: 'compliance', label: 'Compliance Record' },
    { value: 'personal', label: 'Personal Document' }
  ];

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file?.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/webp',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (!allowedTypes?.includes(file?.type)) {
        setError('Invalid file type. Allowed: PDF, JPEG, PNG, WEBP, DOC, DOCX');
        return;
      }

      setError('');
      setFormData(prev => ({ ...prev, file }));

      // Auto-fill document name if empty
      if (!formData?.documentName) {
        setFormData(prev => ({
          ...prev,
          documentName: file?.name?.replace(/\.[^/.]+$/, '')
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (!formData?.file) {
      setError('Please select a file to upload');
      return;
    }

    if (!formData?.employeeId) {
      setError('Please select an employee');
      return;
    }

    if (!formData?.documentName) {
      setError('Please enter a document name');
      return;
    }

    try {
      setUploading(true);

      const documentData = {
        category: formData?.category,
        name: formData?.documentName,
        date: formData?.documentDate,
        description: formData?.description,
        tags: formData?.tags ? formData?.tags?.split(',')?.map(t => t?.trim())?.filter(Boolean) : [],
        uploadedBy: user?.email,
        metadata: {}
      };

      await documentManagementService?.uploadDocument(
        formData?.file,
        formData?.employeeId,
        documentData
      );

      onSuccess();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Upload Document</h2>
            <p className="text-sm text-gray-600 mt-1">Add a new document to the system</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload className="w-12 h-12 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {formData?.file ? formData?.file?.name : 'Click to upload or drag and drop'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  PDF, JPEG, PNG, WEBP, DOC, DOCX (Max 10MB)
                </span>
              </label>
            </div>
          </div>

          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee <span className="text-red-500">*</span>
            </label>
            <select
              value={formData?.employeeId}
              onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              required
            >
              <option value="">Select an employee</option>
              {employees?.map(emp => (
                <option key={emp?.id} value={emp?.id}>
                  {emp?.firstName} {emp?.lastName} - {emp?.employeeNumber}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData?.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              required
            >
              {categories?.map(cat => (
                <option key={cat?.value} value={cat?.value}>
                  {cat?.label}
                </option>
              ))}
            </select>
          </div>

          {/* Document Name */}
          <Input
            label="Document Name"
            required
            value={formData?.documentName}
            onChange={(e) => setFormData(prev => ({ ...prev, documentName: e?.target?.value }))}
            placeholder="Enter document name"
          />

          {/* Document Date */}
          <Input
            type="date"
            label="Document Date"
            value={formData?.documentDate}
            onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e?.target?.value }))}
          />

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              placeholder="Enter document description (optional)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Tags */}
          <Input
            label="Tags"
            value={formData?.tags}
            onChange={(e) => setFormData(prev => ({ ...prev, tags: e?.target?.value }))}
            placeholder="Enter tags separated by commas (e.g., payroll, 2025, december)"
            description="Tags help organize and search documents"
          />

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={uploading}
              iconName="Upload"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUploadModal;